import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

// Supplements aren't a trend you watch climb — the meaningful signal is
// consistency (did you take it?) and dose (how much, and when did it change).
// So the hero is a dose-aware adherence grid: one row per supplement, one cell
// per day. A filled gold cell = logged that day, its opacity scaled to the dose
// relative to that supplement's own max — so a 1000mg→500mg step reads as the
// row visibly dimming, not just a number buried in a table.

import { RangeSelector, useTimeRange } from './TimeRange';

const GRID_MAX_DAYS = 90; // a wider grid stops being legible; stats still use the full range

const GOLD = '#FFB800';

// ISO date (YYYY-MM-DD) for `n` days before today, local time.
const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

const normalizeName = (s) => (s || '').trim().toLowerCase();

const prettyName = (s) => {
  const t = (s || '').trim();
  if (!t) return 'Supplement';
  return t.charAt(0).toUpperCase() + t.slice(1);
};

const formatDose = (dose, unit) => {
  if (dose == null) return null;
  const n = Number.isInteger(dose) ? dose : Math.round(dose * 100) / 100;
  return `${n}${unit ? ` ${unit}` : ''}`;
};

const cellDateLabel = (dateStr) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });

const SupplementsDataView = () => {
  const [entries, setEntries] = useState([]);
  const [regimen, setRegimen] = useState([]);
  const [loading, setLoading] = useState(true);
  const { spanDays, selectorProps } = useTimeRange('1M');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entryRes, regRes] = await Promise.all([
        apiClient.get('/api/health/entries', {
          params: { category: 'nutrition.supplement', limit: 1000 },
        }),
        apiClient.get('/api/regimen', { params: { kind: 'supplement' } }),
      ]);
      setEntries(entryRes.data || []);
      setRegimen(regRes.data || []);
    } catch (error) {
      console.error('Error fetching supplements:', error);
      toast.error('Failed to load supplements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Finite window → its length in days; All / incomplete-custom → null (unbounded),
  // which the grid/windowStart logic below already treats as "everything".
  const rangeDays = Number.isFinite(spanDays) ? spanDays : null;

  // The list of day-columns the grid draws (oldest → newest), capped for legibility.
  const gridDates = useMemo(() => {
    const span = Math.min(rangeDays || 3650, GRID_MAX_DAYS);
    return Array.from({ length: span }, (_, i) => isoDaysAgo(span - 1 - i));
  }, [rangeDays]);

  const windowStart = useMemo(
    () => (rangeDays ? isoDaysAgo(rangeDays - 1) : '0000-01-01'),
    [rangeDays]
  );

  // One group per supplement: prefer the regimen link, else the name. Every
  // active regimen supplement gets a group even with no intake yet, so a stack
  // item you've stopped taking shows an empty (missed) row rather than vanishing.
  const supplements = useMemo(() => {
    const groups = new Map();

    const keyFor = (regimenItemId, name) =>
      regimenItemId != null ? `reg:${regimenItemId}` : `name:${normalizeName(name)}`;

    const ensure = (key, displayName) => {
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          name: displayName,
          onStack: false,
          plannedDose: null,
          plannedUnit: null,
          byDate: new Map(), // date -> { dose, unit }
        });
      }
      return groups.get(key);
    };

    // Seed from the standing stack first (so planned dose + on-stack flag exist).
    regimen.forEach((item) => {
      if (item.active === false) return;
      const g = ensure(keyFor(item.id, item.name), prettyName(item.name));
      g.onStack = true;
      g.plannedDose = item.dose;
      g.plannedUnit = item.dose_unit;
      g.regimenName = normalizeName(item.name);
    });

    // Fold in actual intake events. Match to a stack row by regimen_item_id, else
    // by name, else create an ad-hoc group.
    entries.forEach((e) => {
      const data = e.data || {};
      const name = data.supplement_name || e.notes || e.entry_type;
      let key = keyFor(data.regimen_item_id, name);
      // Ad-hoc log whose name matches a stack item → merge into that stack row.
      if (data.regimen_item_id == null) {
        const match = [...groups.values()].find(
          (g) => g.regimenName && g.regimenName === normalizeName(name)
        );
        if (match) key = match.key;
      }
      const g = ensure(key, prettyName(name));
      const prev = g.byDate.get(e.entry_date);
      // Multiple logs same day → keep the largest dose (best-effort daily total is
      // noisy for splits; dose magnitude is what the grid encodes).
      if (!prev || (e.value != null && e.value > (prev.dose ?? -Infinity))) {
        g.byDate.set(e.entry_date, { dose: e.value, unit: e.value_unit });
      }
    });

    // Derive per-supplement summary within the current window.
    const list = [...groups.values()].map((g) => {
      const dosed = [...g.byDate.entries()].filter(([d]) => d >= windowStart);
      const doses = dosed.map(([, v]) => v.dose).filter((d) => d != null);
      const maxDose = doses.length ? Math.max(...doses) : null;
      const takenDates = new Set(dosed.map(([d]) => d));
      const sortedDosed = dosed
        .filter(([, v]) => v.dose != null)
        .sort((a, b) => (a[0] < b[0] ? -1 : 1));
      const latest = sortedDosed.length ? sortedDosed[sortedDosed.length - 1][1] : null;
      const earliest = sortedDosed.length ? sortedDosed[0][1] : null;
      const doseChanged =
        earliest && latest && earliest.dose !== latest.dose;

      // Adherence denominator: days in the visible grid where we'd expect it —
      // from the later of (grid start, first time we ever saw it) to today.
      const firstSeen = g.byDate.size
        ? [...g.byDate.keys()].sort()[0]
        : null;
      const denomStart =
        firstSeen && firstSeen > gridDates[0] ? firstSeen : gridDates[0];
      const expectedDays = gridDates.filter((d) => d >= denomStart).length || 1;
      const takenInGrid = gridDates.filter((d) => takenDates.has(d)).length;

      return {
        ...g,
        maxDose,
        latest,
        earliest,
        doseChanged,
        takenInGrid,
        expectedDays,
        adherencePct: Math.round((takenInGrid / expectedDays) * 100),
      };
    });

    // On-stack first, then by adherence descending.
    return list.sort((a, b) => {
      if (a.onStack !== b.onStack) return a.onStack ? -1 : 1;
      return b.adherencePct - a.adherencePct;
    });
  }, [entries, regimen, gridDates, windowStart]);

  const totalLogged = entries.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!supplements.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-gray-700 dark:text-gray-200 font-medium mb-2">
            No supplements yet
          </p>
          <p className="text-center text-sm text-gray-600 dark:text-gray-300 max-w-md mx-auto">
            Log an intake in plain language — say or type{' '}
            <span className="font-medium text-gray-800 dark:text-gray-100">
              "took 500 mg magnesium"
            </span>{' '}
            — or build a standing stack on the Supplements page. Your dose &amp;
            adherence history will show up here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Range selector + legend */}
      <RangeSelector
        {...selectorProps}
        right={(
          <span className="shrink-0 text-sm text-gray-600 dark:text-gray-300">
            {supplements.length} supplement{supplements.length === 1 ? '' : 's'} ·{' '}
            {totalLogged} logged intake{totalLogged === 1 ? '' : 's'}
          </span>
        )}
      />

      {/* Dose-aware adherence grid */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Adherence &amp; dose</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Each cell is a day; a filled cell means it was logged. Brighter = a
            bigger dose relative to that supplement's own range, so a dose change
            shows as the row getting lighter or darker.
            {gridDates.length >= GRID_MAX_DAYS && ' Showing the most recent 90 days.'}
          </p>

          <div className="space-y-4">
            {supplements.map((s) => (
              <div key={s.key}>
                <div className="flex items-baseline justify-between gap-3 mb-1.5 flex-wrap">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {s.name}
                    </span>
                    {s.onStack && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        on your stack
                      </span>
                    )}
                    {s.doseChanged && s.earliest && s.latest && (
                      <span className="text-sm text-amber-600 dark:text-amber-400">
                        {formatDose(s.earliest.dose, s.earliest.unit)} →{' '}
                        {formatDose(s.latest.dose, s.latest.unit)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-3">
                    {s.latest && !s.doseChanged && (
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {formatDose(s.latest.dose, s.latest.unit)}
                      </span>
                    )}
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200 tabular-nums">
                      {s.takenInGrid}/{s.expectedDays} days · {s.adherencePct}%
                    </span>
                  </div>
                </div>

                {/* Day cells — horizontal strip, scrolls on narrow screens */}
                <div className="overflow-x-auto">
                  <div className="flex gap-[3px] min-w-min">
                    {gridDates.map((d) => {
                      const hit = s.byDate.get(d);
                      let bg = 'rgba(148,163,184,0.18)'; // slate-400 @ low = not logged
                      let opacity = 1;
                      if (hit) {
                        const rel =
                          s.maxDose && hit.dose != null
                            ? Math.max(0.4, hit.dose / s.maxDose)
                            : 0.9;
                        bg = GOLD;
                        opacity = rel;
                      }
                      const title = hit
                        ? `${cellDateLabel(d)} · ${formatDose(hit.dose, hit.unit) || 'logged'}`
                        : `${cellDateLabel(d)} · not logged`;
                      return (
                        <div
                          key={d}
                          title={title}
                          className="rounded-sm shrink-0"
                          style={{
                            width: 10,
                            height: 22,
                            backgroundColor: bg,
                            opacity,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupplementsDataView;
